import { type PromoFeatureWithAccess } from '@luxuryescapes/contract-svc-promo';

type DisabledReason = { kind: 'unknown' | 'denied' | 'environment'; message: string };

/** Explain the evaluated requirements, preserving alternatives within each parent/feature group. */
export function getPromoFeatureDisabledReason(
  feature: PromoFeatureWithAccess | undefined,
  isProduction: boolean,
): DisabledReason | undefined {
  if (!feature) {
    return { kind: 'unknown', message: 'Feature access could not be verified.' };
  }
  if (feature.isOnlyTest && isProduction) {
    return { kind: 'environment', message: `${feature.displayName} is only available in the test environment.` };
  }
  if (feature.canUse === true) {
    return undefined;
  }
  if (feature.canUse === 'unknown') {
    return {
      kind: 'unknown',
      message: `${feature.displayName}: access could not be verified. Required roles or other access information may still be loading.`,
    };
  }

  const groups = new Map<string, Array<PromoFeatureWithAccess['accessReasons'][number]>>();
  feature.accessReasons.forEach((reason) => {
    groups.set(reason.featureKey, [...(groups.get(reason.featureKey) ?? []), reason]);
  });
  const requirements = [...groups.values()].flatMap((reasons) => {
    if (reasons[0]?.operator === 'or' && reasons.some((reason) => reason.result === true)) {
      return [];
    }
    const unmet = reasons
      .filter((reason) => reason.result !== true)
      .map(({ requirement }) => {
        switch (requirement.type) {
          case 'auth_role':
          case 'promo_role':
            return `${requirement.key.replace(/[-_]/g, ' ')} role`;
          case 'optIn':
            return 'a session opt-in from the Promo Features page';
          case 'promoBrand':
            return `one of these promo brands: ${requirement.brands.join(', ')}`;
          case 'region':
            return `one of these regions: ${requirement.regions.join(', ')}`;
          case 'testEnvironment':
            return 'the test environment';
          case 'publicFeature':
            return 'a feature available to this client';
        }
      });
    return unmet.length ? [unmet.join(reasons[0]?.operator === 'or' ? ' or ' : ' and ')] : [];
  });
  return {
    kind: 'denied',
    message: requirements.length
      ? `${feature.displayName} requires ${requirements.join(' and ')}.`
      : `You do not meet the access requirements for ${feature.displayName}.`,
  };
}
